/** Ký tự mỗi token theo hệ chữ (plan §3.2). Hangul gộp với CJK: cùng mật độ token thô. */
export const CHARS_PER_TOKEN = { latin: 3.2, cjk: 1.1, thai: 2.2 };
const VIETNAMESE = /[ăâđêôơưàáảãạằắẳẵặầấẩẫậèéẻẽẹềếểễệìíỉĩịòóỏõọồốổỗộờớởỡợùúủũụừứửữựỳýỷỹỵ]/i;
const VIETNAMESE_ALL = /[ăâđêôơưàáảãạằắẳẵặầấẩẫậèéẻẽẹềếểễệìíỉĩịòóỏõọồốổỗộờớởỡợùúủũụừứửữựỳýỷỹỵ]/gi;
const KANA = /[぀-ヿ]/;
const HAN = /[㐀-鿿豈-﫿]/;
const HANGUL = /[가-힯ᄀ-ᇿ㄰-㆏]/;
const THAI = /[฀-๿]/;
const LATIN = /[a-zÀ-ɏḀ-ỿ]/i;
/** Đếm ký tự theo hệ chữ (bỏ khoảng trắng, số, dấu câu — chúng không quyết định hệ chữ). */
function scriptCounts(text) {
    const counts = { latin: 0, cjk: 0, thai: 0, kana: 0, hangul: 0 };
    for (const ch of text) {
        if (THAI.test(ch))
            counts.thai += 1;
        else if (KANA.test(ch)) {
            counts.cjk += 1;
            counts.kana += 1;
        }
        else if (HANGUL.test(ch)) {
            counts.cjk += 1;
            counts.hangul += 1;
        }
        else if (HAN.test(ch))
            counts.cjk += 1;
        else if (LATIN.test(ch))
            counts.latin += 1;
    }
    return counts;
}
function dominantScript(counts) {
    if (counts.cjk >= counts.latin && counts.cjk >= counts.thai && counts.cjk > 0)
        return 'cjk';
    if (counts.thai > counts.latin)
        return 'thai';
    return 'latin';
}
/**
 * Share of Latin letters carrying a Vietnamese diacritic above which a Latin text reads as
 * Vietnamese. A Vietnamese sentence sits at 25–45 %; an English sentence quoting one Vietnamese
 * name ("send it to Hà") stays far below.
 */
const VIETNAMESE_SHARE = 0.08;
/** Ngôn ngữ nguồn theo hệ chữ chiếm đa số. Latin có đủ dấu tiếng Việt ⇒ `vi`. */
export function guessSourceLang(text) {
    const counts = scriptCounts(text);
    const script = dominantScript(counts);
    if (script === 'thai')
        return 'th';
    if (script === 'cjk') {
        if (counts.kana > 0)
            return 'ja';
        if (counts.hangul > counts.cjk / 2)
            return 'ko';
        return 'zh';
    }
    if (!VIETNAMESE.test(text))
        return 'latin';
    // One accented letter in an English sentence (a name) is not Vietnamese.
    const marked = text.match(VIETNAMESE_ALL)?.length ?? 0;
    return counts.latin > 0 && marked / counts.latin >= VIETNAMESE_SHARE ? 'vi' : 'latin';
}
/** Token nguồn ước lượng: ký tự / mật độ của hệ chữ chiếm đa số (plan §3.2). */
export function estimateSourceTokens(text) {
    const script = dominantScript(scriptCounts(text));
    const perToken = script === 'cjk'
        ? CHARS_PER_TOKEN.cjk
        : script === 'thai'
            ? CHARS_PER_TOKEN.thai
            : CHARS_PER_TOKEN.latin;
    return text.length / perToken;
}
/**
 * "Cỡ" của một khối tính bằng **ký tự Latin tương đương** — token ước lượng × 3.2. Một khối
 * tiếng Việt 300 ký tự nặng 300; một khối tiếng Nhật 300 ký tự nặng ~870 (≈ 270 token).
 */
export function translationWeight(text) {
    return Math.ceil(estimateSourceTokens(text) * CHARS_PER_TOKEN.latin);
}
/**
 * Ngân sách **một trang** khi gom khối để dịch một lượt — APM-BIL-page, plan §3.4.
 *
 * 3 000 ký tự Latin tương đương ≈ 500 từ tiếng Anh / ~600 từ tiếng Việt ≈ một trang A4 cỡ chữ
 * 11–12 (≈ 940 token nguồn). Vì sao không lớn hơn:
 *
 * - **Trần output** của một lượt là 4 096 token. Trang này sang ngôn ngữ giãn nhiều nhất (Nhật/Hoa
 *   → Việt, ×1.6, hệ số an toàn ×1.4) được trần ~2 350 token (< 60 % của 4 096), nên lượt chạy
 *   lại khi bị cắt (×2, kẹp 4 096) vẫn có thêm ~75 % chỗ. Trang lớn hơn thì lượt chạy lại không
 *   còn chỗ để lớn lên.
 * - **Phần tiết kiệm đã bão hoà**: thứ gom lại tiết kiệm là system prompt + schema tool + vỏ
 *   `tool_use` lặp lại ở mỗi lượt (~1 000 token input, ~65 token output). Ở một trang mỗi lượt,
 *   phần cố định ấy chỉ còn ~1/2 input của lượt; gom gấp đôi chỉ bớt thêm vài phần trăm, mà một
 *   lượt hỏng (bị cắt, model trả thiếu khối) thì phải làm lại gấp đôi chữ.
 * - Người đọc chờ từng lượt: một trang ~10–20 giây với tầng model nhỏ.
 */
export const TRANSLATION_PAGE_WEIGHT = 3_000;
/** Số khối tối đa của một lượt — trang toàn tiêu đề ngắn vẫn không thành 200 khối một lượt. */
export const MAX_TRANSLATION_BATCH_BLOCKS = 40;
const ENGLISH_WORDS = new Set([
    'the',
    'and',
    'of',
    'to',
    'is',
    'are',
    'in',
    'for',
    'with',
    'that',
    'this',
    'on',
    'be',
    'it',
    'as',
    'by',
    'from',
    'will',
    'was',
    'we',
    'you',
    'our',
    'an',
    'or',
    'at',
    'over',
    'has',
    'have',
    'not',
    'can',
    'all',
    'its',
    'their',
    'which',
    'if',
    'but',
    'been',
    'were',
    'into',
    'each',
]);
/**
 * Câu tiếng Anh: ≥ 4 từ và ≥ 15 % là từ chức năng tiếng Anh trong danh sách trên (văn xuôi Anh
 * thường 30–45 %; tiếng Pháp / Đức / Tây Ban Nha gần 0 vì danh sách không có từ nào của chúng).
 */
function looksEnglish(text) {
    const words = text.toLowerCase().match(/[a-z']+/g) ?? [];
    if (words.length < 4)
        return false;
    const common = words.filter((word) => ENGLISH_WORDS.has(word)).length;
    return common / words.length >= 0.15;
}
/** Ngôn ngữ đích mà một hệ chữ nguồn **chắc chắn** trùng — Latin không dấu thì chỉ đoán tiếng Anh. */
const SAME_LANG = {
    vi: 'vi',
    ja: 'ja',
    zh: 'zh-Hans',
    ko: 'ko',
    th: 'th',
};
/**
 * Khối đã ở đúng ngôn ngữ đích — không gửi cho model. Chỉ nói "có" khi chắc: tiếng Pháp / Đức /
 * Tây Ban Nha không phân biệt được bằng hệ chữ nên luôn "không" (model tự trả nguyên văn theo luật
 * của prompt), và một tiêu đề tiếng Anh ba từ cũng "không".
 */
export function isAlreadyInLang(text, target) {
    const source = guessSourceLang(text);
    if (source === 'latin')
        return target === 'en' && looksEnglish(text);
    return SAME_LANG[source] === target;
}
/**
 * Ngôn ngữ chiếm đa số của cả tài liệu, khi đoán chắc được — web không cho chọn nó làm ngôn ngữ
 * đích. `null` = không chắc (Latin không dấu mà không giống tiếng Anh, tài liệu rỗng).
 */
export function documentLangOf(texts) {
    const text = texts.join('\n');
    if (!/\p{L}/u.test(text))
        return null;
    const source = guessSourceLang(text);
    if (source === 'latin')
        return looksEnglish(text) ? 'en' : null;
    return SAME_LANG[source];
}
//# sourceMappingURL=translation-text.js.map